// src/components/accountability/CanvasserLocationMap.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { GoogleMap, Marker, InfoWindow, Polyline } from '@react-google-maps/api';
import { Users, Clock, MapPin, Activity } from 'lucide-react';
import { locationService, CanvasserLocationUpdate, AccountabilityRecord } from '../../services/location.service';
import { Timestamp } from 'firebase/firestore';

interface CanvasserLocationMapProps {
  projectId?: string;
  height?: string;
  showLegend?: boolean;
  rewindMode?: boolean;
  rewindDate?: Date;
}

const PATH_COLORS = ['#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899', '#06B6D4', '#F97316'];

const mapContainerStyle = {
  width: '100%',
  height: '100%',
};

const defaultCenter = {
  lat: 39.8283,
  lng: -98.5795,
};

const CanvasserLocationMap: React.FC<CanvasserLocationMapProps> = ({
  projectId,
  height = '500px',
  showLegend = true,
  rewindMode = false,
  rewindDate,
}) => {
  const [canvasserLocations, setCanvasserLocations] = useState<CanvasserLocationUpdate[]>([]);
  const [selectedCanvasser, setSelectedCanvasser] = useState<CanvasserLocationUpdate | null>(null);
  const [mapRef, setMapRef] = useState<google.maps.Map | null>(null);
  const [historicalPaths, setHistoricalPaths] = useState<Map<string, { userName: string; records: AccountabilityRecord[] }>>(new Map());

  // Live mode subscription
  useEffect(() => {
    if (rewindMode) return;
    const unsubscribe = locationService.subscribeToCanvasserLocations(setCanvasserLocations);
    return () => unsubscribe();
  }, [projectId, rewindMode]);

  // Rewind mode data loading
  useEffect(() => {
    if (!rewindMode || !projectId || !rewindDate) return;

    const loadHistorical = async () => {
      try {
        const paths = await locationService.getHistoricalCanvasserPaths(projectId, rewindDate);
        setHistoricalPaths(paths);
      } catch (error) {
        console.error('Error loading historical paths:', error);
      }
    };

    loadHistorical();
  }, [rewindMode, projectId, rewindDate]);

  useEffect(() => {
    if (!mapRef) return;
    const bounds = new google.maps.LatLngBounds();
    let hasPoints = false;

    if (rewindMode) {
      historicalPaths.forEach(({ records }) => {
        records.forEach((r) => {
          bounds.extend({ lat: r.capturedLocation.lat, lng: r.capturedLocation.lng });
          hasPoints = true;
        });
      });
    } else {
      canvasserLocations.forEach((loc) => {
        bounds.extend({ lat: loc.location.lat, lng: loc.location.lng });
        hasPoints = true;
      });
    }

    if (hasPoints) {
      mapRef.fitBounds(bounds);
    }
  }, [mapRef, canvasserLocations, rewindMode, historicalPaths]);

  const onMapLoad = useCallback((map: google.maps.Map) => {
    setMapRef(map);
  }, []);

  const getMarkerIcon = (status: 'active' | 'idle' | 'offline'): google.maps.Symbol => {
    const colors = {
      active: '#22c55e', // green
      idle: '#f59e0b', // yellow
      offline: '#9ca3af', // gray
    };

    return {
      path: google.maps.SymbolPath.CIRCLE,
      fillColor: colors[status],
      fillOpacity: 1,
      strokeColor: '#ffffff',
      strokeWeight: 2,
      scale: 10,
    };
  };

  const formatTimestamp = (timestamp: Timestamp) => {
    const date = timestamp.toDate();
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    return date.toLocaleDateString();
  };

  const center = canvasserLocations.length > 0
    ? {
        lat: canvasserLocations.reduce((sum, loc) => sum + loc.location.lat, 0) / canvasserLocations.length,
        lng: canvasserLocations.reduce((sum, loc) => sum + loc.location.lng, 0) / canvasserLocations.length,
      }
    : defaultCenter;

  const activeCount = canvasserLocations.filter((c) => c.status === 'active').length;
  const idleCount = canvasserLocations.filter((c) => c.status === 'idle').length;

  return (
    <div className="relative" style={{ height }}>
      {/* Stats Overlay */}
      <div className="absolute top-4 left-4 z-10 bg-white rounded-lg shadow-lg p-3 space-y-2">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-gray-600" />
          <span className="text-sm font-medium">{canvasserLocations.length} Canvassers</span>
        </div>
        <div className="flex items-center gap-3 text-xs">
          <div className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-green-500"></span>
            <span>{activeCount} Active</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
            <span>{idleCount} Idle</span>
          </div>
        </div>
      </div>

      {/* Legend */}
      {showLegend && (
        <div className="absolute bottom-4 right-4 z-10 bg-white rounded-lg shadow-lg p-3">
          <h4 className="text-xs font-medium text-gray-700 mb-2">Legend</h4>
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-green-500"></span>
              <span className="text-xs text-gray-600">Active (recent activity)</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-yellow-500"></span>
              <span className="text-xs text-gray-600">Idle (&gt;5 min inactive)</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-gray-400"></span>
              <span className="text-xs text-gray-600">Offline</span>
            </div>
          </div>
        </div>
      )}

      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        center={center}
        zoom={canvasserLocations.length > 0 ? 12 : 4}
        onLoad={onMapLoad}
        options={{
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: true,
        }}
      >
        {/* Live mode markers */}
        {!rewindMode && canvasserLocations.map((canvasser) => (
          <Marker
            key={canvasser.userId}
            position={{ lat: canvasser.location.lat, lng: canvasser.location.lng }}
            icon={getMarkerIcon(canvasser.status)}
            onClick={() => setSelectedCanvasser(canvasser)}
            title={canvasser.userName}
          />
        ))}

        {/* Rewind mode polylines and markers */}
        {rewindMode && Array.from(historicalPaths.entries()).map(([userId, data], index) => {
          const color = PATH_COLORS[index % PATH_COLORS.length];
          const path = data.records.map(r => ({
            lat: r.capturedLocation.lat,
            lng: r.capturedLocation.lng,
          }));
          return (
            <React.Fragment key={userId}>
              <Polyline
                path={path}
                options={{
                  strokeColor: color,
                  strokeOpacity: 0.8,
                  strokeWeight: 3,
                }}
              />
              {data.records.map((record, i) => (
                <Marker
                  key={`${userId}-${i}`}
                  position={{ lat: record.capturedLocation.lat, lng: record.capturedLocation.lng }}
                  icon={{
                    path: google.maps.SymbolPath.CIRCLE,
                    fillColor: color,
                    fillOpacity: 0.9,
                    strokeColor: '#ffffff',
                    strokeWeight: 1,
                    scale: 5,
                  }}
                  title={`${data.userName} - ${record.action} (${record.timestamp?.toDate?.()?.toLocaleTimeString() || ''})`}
                />
              ))}
            </React.Fragment>
          );
        })}

        {selectedCanvasser && (
          <InfoWindow
            position={{
              lat: selectedCanvasser.location.lat,
              lng: selectedCanvasser.location.lng,
            }}
            onCloseClick={() => setSelectedCanvasser(null)}
          >
            <div className="p-2 min-w-[200px]">
              <h3 className="font-medium text-gray-900 mb-2">{selectedCanvasser.userName}</h3>
              <div className="space-y-1.5 text-sm text-gray-600">
                <div className="flex items-center gap-1.5">
                  <Activity className="w-3.5 h-3.5" />
                  <span>
                    Status:{' '}
                    <span
                      className={`font-medium ${
                        selectedCanvasser.status === 'active'
                          ? 'text-green-600'
                          : selectedCanvasser.status === 'idle'
                          ? 'text-yellow-600'
                          : 'text-gray-500'
                      }`}
                    >
                      {selectedCanvasser.status}
                    </span>
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <MapPin className="w-3.5 h-3.5" />
                  <span>
                    Accuracy: ±{Math.round(selectedCanvasser.accuracy)}m
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5" />
                  <span>Last update: {formatTimestamp(selectedCanvasser.lastActivityAt)}</span>
                </div>
                {selectedCanvasser.currentWorkloadId && (
                  <div className="pt-1 mt-1 border-t">
                    <span className="text-xs text-gray-500">
                      Working on workload: {selectedCanvasser.currentWorkloadId.slice(0, 8)}...
                    </span>
                  </div>
                )}
              </div>
            </div>
          </InfoWindow>
        )}
      </GoogleMap>

      {/* Empty State */}
      {!rewindMode && canvasserLocations.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100/80">
          <div className="text-center">
            <Users className="w-12 h-12 mx-auto mb-2 text-gray-400" />
            <p className="text-gray-600 font-medium">No Active Canvassers</p>
            <p className="text-sm text-gray-500">Canvasser locations will appear here when they're in the field</p>
          </div>
        </div>
      )}
      {rewindMode && historicalPaths.size === 0 && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100/80">
          <div className="text-center">
            <Users className="w-12 h-12 mx-auto mb-2 text-gray-400" />
            <p className="text-gray-600 font-medium">No Activity Found</p>
            <p className="text-sm text-gray-500">No canvasser activity was recorded on this date</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default CanvasserLocationMap;
